import { useEffect, useState } from 'react';
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  SelectSeparator
} from "@/components/ui/select";
import { PlusCircle, Search } from 'lucide-react';
import { Input } from "@/components/ui/input";

export interface SelectorItem {
  id: number;
  title: string | null;
}

interface ItemSelectorProps {
  entityName: string;
  displayName: string;
  items: SelectorItem[];
  selectedId?: number | null;
  onSelect: (id: number | null) => void;
  loading?: boolean;
  onSearch?: (searchTerm: string) => void;
}

export function ItemSelector({
  entityName,
  displayName,
  items = [],
  selectedId,
  onSelect,
  loading = false,
  onSearch
}: ItemSelectorProps) {
  // State for search term
  const [searchTerm, setSearchTerm] = useState('');
  
  // Take only 10 most recent items
  const recentItems = items?.slice(0, 10) || [];
  
  // Filter items based on search term
  const filteredItems = searchTerm.trim() === '' 
    ? recentItems 
    : items?.filter(item => 
        (item.title || `Untitled ${displayName}`)
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ) || [];
  
  // Use internal state to track the selected value for the select component
  const [selectedValue, setSelectedValue] = useState<string>("new");
  
  // Update internal state when selectedId changes
  useEffect(() => {
    if (selectedId === null || selectedId === undefined) {
      setSelectedValue("new");
    } else {
      // Validate that the selected item exists in the items array
      const itemExists = items.some(item => item.id === selectedId);
      if (itemExists) {
        setSelectedValue(selectedId.toString());
      } else {
        // If item doesn't exist, reset to "new" (item might not be loaded yet)
        setSelectedValue("new");
      }
    }
  }, [selectedId, items]);
  
  const handleValueChange = (value: string) => {
    setSelectedValue(value);
    
    if (value === "new") {
      onSelect(null);
      return;
    }
    
    if (value === "none" || !value || value.startsWith('item-')) {
      return;
    }
    
    const numericValue = Number(value);
    if (!isNaN(numericValue)) {
      onSelect(numericValue);
    }
  };
  
  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Call onSearch callback if provided
    if (onSearch) {
      onSearch(value);
    }
  };
  
  // Prevent search input from closing the dropdown
  const handleSearchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  
  return (
    <div className="flex flex-col space-y-1.5 selector-highlight">
      <Select
        value={selectedValue}
        onValueChange={handleValueChange}
        disabled={loading}
        defaultValue="new"
      >
        <SelectTrigger className="w-full bg-background border-border hover:bg-accent hover:text-accent-foreground transition-colors">
          <SelectValue placeholder={`New ${displayName}`} />
        </SelectTrigger>
        <SelectContent className="overflow-y-auto max-h-[300px] min-h-[150px] selector-content-animate">
          <SelectItem value="new" className="text-primary font-medium border-l-2 border-primary pl-2 hover:bg-primary/10">
            <div className="flex items-center">
              <PlusCircle className="mr-2 h-4 w-4" />
              <span>New {displayName}</span>
            </div>
          </SelectItem>
          
          <div className="px-2 py-2" onClick={handleSearchClick}>
            <div className="flex items-center px-2 rounded-md border border-input focus-within:ring-1 focus-within:ring-ring">
              <Search className="h-3.5 w-3.5 text-muted-foreground mr-2" />
              <Input
                placeholder={`Search ${displayName.toLowerCase()}s...`}
                className="h-8 px-0 border-0 focus-visible:outline-none focus-visible:ring-0 shadow-none"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          
          {filteredItems.length > 0 && <SelectSeparator className="my-1" />}
          
          <SelectGroup>
            {filteredItems.length > 0 && (
              <SelectLabel className="text-xs font-medium text-muted-foreground px-2 py-1.5">
                {searchTerm ? 'Search Results' : `Recent ${displayName}s`}
              </SelectLabel>
            )}
            {filteredItems.length === 0 && (
              <SelectItem value="none" disabled>
                <span className="text-muted-foreground italic">
                  {searchTerm ? `No ${entityName.toLowerCase()}s found` : `No ${entityName.toLowerCase()}s available`}
                </span>
              </SelectItem>
            )}
            {filteredItems.map((item) => (
              <SelectItem 
                key={item.id} 
                value={item.id ? item.id.toString() : 'item-' + Math.random()} 
                className="flex items-center"
              >
                {item.title || `Untitled ${displayName}`}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
} 